import { useCallback, useEffect, useRef, useState } from 'react';

interface WebSpeechRecognitionEvent extends Event {
    results: WebSpeechRecognitionResultList;
    resultIndex: number;
}

interface WebSpeechRecognitionResultList {
    length: number;
    [index: number]: WebSpeechRecognitionResult;
}

interface WebSpeechRecognitionResult {
    [index: number]: WebSpeechRecognitionAlternative;
    isFinal: boolean;
}

interface WebSpeechRecognitionAlternative {
    transcript: string;
}

interface WebSpeechRecognitionErrorEvent extends Event {
    error: string;
}

interface WebSpeechRecognition extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    onresult: ((event: WebSpeechRecognitionEvent) => void) | null;
    onerror: ((event: WebSpeechRecognitionErrorEvent) => void) | null;
    onend: (() => void) | null;
    start(): void;
    stop(): void;
    abort(): void;
}

interface WebSpeechRecognitionConstructor {
    new (): WebSpeechRecognition;
}

interface WebSpeechRecognitionWindow {
    SpeechRecognition?: WebSpeechRecognitionConstructor;
    webkitSpeechRecognition?: WebSpeechRecognitionConstructor;
}

interface UseSpeechRecognitionOptions {
    disabled?: boolean;
    language?: string;
    onTranscript: (transcript: string) => void;
    onError?: (error: string) => void;
}

const getSpeechRecognitionConstructor = () => {
    if (typeof window === 'undefined') return undefined;
    const speechWindow = window as unknown as WebSpeechRecognitionWindow;
    return speechWindow.SpeechRecognition || speechWindow.webkitSpeechRecognition;
};

export const useSpeechRecognition = ({ disabled = false, language, onTranscript, onError }: UseSpeechRecognitionOptions) => {
    const [isListening, setIsListening] = useState(false);
    const recognitionRef = useRef<WebSpeechRecognition | null>(null);
    const onTranscriptRef = useRef(onTranscript);
    const onErrorRef = useRef(onError);
    const isSupported = Boolean(getSpeechRecognitionConstructor());

    useEffect(() => {
        onTranscriptRef.current = onTranscript;
    }, [onTranscript]);

    useEffect(() => {
        onErrorRef.current = onError;
    }, [onError]);

    const finishListening = useCallback((abort = false) => {
        const recognition = recognitionRef.current;
        recognitionRef.current = null;
        setIsListening(false);
        if (!recognition) return;

        if (abort) {
            recognition.onresult = null;
            recognition.onerror = null;
            recognition.onend = null;
            recognition.abort();
        } else {
            recognition.stop();
        }
    }, []);

    const startListening = useCallback(() => {
        if (disabled || recognitionRef.current) return;

        const SpeechRecognitionAPI = getSpeechRecognitionConstructor();
        if (!SpeechRecognitionAPI) return;

        try {
            const recognition = new SpeechRecognitionAPI();
            recognition.continuous = true;
            recognition.interimResults = true;
            recognition.lang = language || navigator.language || 'en-US';

            recognition.onresult = (event) => {
                const finalTranscript = Array.from(
                    { length: event.results.length - event.resultIndex },
                    (_, offset) => event.results[event.resultIndex + offset]
                )
                    .filter((result) => result.isFinal)
                    .map((result) => result[0]?.transcript ?? '')
                    .join(' ')
                    .trim();

                if (finalTranscript) onTranscriptRef.current(finalTranscript);
            };

            recognition.onerror = (event) => {
                if (recognitionRef.current !== recognition) return;
                recognitionRef.current = null;
                setIsListening(false);
                onErrorRef.current?.(event.error);
            };

            recognition.onend = () => {
                if (recognitionRef.current !== recognition) return;
                recognitionRef.current = null;
                setIsListening(false);
            };

            recognitionRef.current = recognition;
            recognition.start();
            setIsListening(true);
        } catch {
            recognitionRef.current = null;
            setIsListening(false);
            onErrorRef.current?.('start-failed');
        }
    }, [disabled, language]);

    const toggleListening = useCallback(() => {
        if (recognitionRef.current) finishListening();
        else startListening();
    }, [finishListening, startListening]);

    useEffect(() => {
        if (disabled) finishListening(true);
    }, [disabled, finishListening]);

    useEffect(() => () => finishListening(true), [finishListening]);

    return {
        isSupported,
        isListening,
        startListening,
        stopListening: () => finishListening(false),
        abortListening: () => finishListening(true),
        toggleListening
    };
};
